import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, X, Loader2, Copy, MessageSquare, AlertCircle, Minus, Maximize2 } from 'lucide-react';
import { Button } from './ui/Button';
import { getSuggestion } from '../lib/aiService';
import { useTranslation } from '../lib/i18n';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  suggestion?: string;
  error?: boolean;
}

interface ChatWidgetProps {
  onClose: () => void;
  documentContent?: string;
  onApplySuggestion?: (suggestion: string) => void;
  canUseAIFeature?: (level: 'basic' | 'advanced' | 'premium') => boolean;
}

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

interface Velocity {
  x: number;
  y: number;
}

const MIN_WIDTH = 400;
const MIN_HEIGHT = 500;
const MAX_WIDTH = Math.min(900, window.innerWidth - 32);
const MAX_HEIGHT = Math.min(800, window.innerHeight - 32);

const ChatWidget: React.FC<ChatWidgetProps> = ({ onClose, documentContent, onApplySuggestion, canUseAIFeature }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState<Size>({ width: 450, height: 650 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  const positionRef = useRef<Position>({ x: 20, y: 20 });
  const velocityRef = useRef<Velocity>({ x: 0, y: 0 });
  const lastMousePosRef = useRef<Position>({ x: 0, y: 0 });
  const lastUpdateTimeRef = useRef(performance.now());
  const animationFrameRef = useRef<number>();
  const resizeStartRef = useRef<Position>({ x: 0, y: 0 });
  const initialSizeRef = useRef<Size>({ width: 450, height: 650 });
  
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<Position>({ x: 0, y: 0 });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      
      if (window.innerWidth < 768) {
        setSize({
          width: window.innerWidth - 16,
          height: Math.min(window.innerHeight * 0.85, MAX_HEIGHT)
        });
        positionRef.current = { x: 8, y: window.innerHeight - size.height - 8 };
      } else {
        const maxX = window.innerWidth - size.width - 16;
        const maxY = window.innerHeight - size.height - 16;
        positionRef.current = {
          x: Math.min(positionRef.current.x, maxX),
          y: Math.min(positionRef.current.y, maxY)
        };
      }
      
      updateTransform(positionRef.current.x, positionRef.current.y);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [size]);

  const updateTransform = useCallback((x: number, y: number) => {
    if (!widgetRef.current) return;
    widgetRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  }, []);

  const getBoundaries = useCallback(() => {
    if (!widgetRef.current) return { maxX: 0, maxY: 0 };
    return {
      maxX: window.innerWidth - widgetRef.current.offsetWidth,
      maxY: window.innerHeight - (isMinimized ? 60 : widgetRef.current.offsetHeight)
    };
  }, [isMinimized]);

  const updatePosition = useCallback((x: number, y: number) => {
    if (!widgetRef.current || isMobile) return;

    const { maxX, maxY } = getBoundaries();
    const newX = Math.max(0, Math.min(x, maxX));
    const newY = Math.max(0, Math.min(y, maxY));

    positionRef.current = { x: newX, y: newY };
    updateTransform(newX, newY);
  }, [getBoundaries, updateTransform, isMobile]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (isMobile) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    resizeStartRef.current = { x: e.clientX, y: e.clientY };
    initialSizeRef.current = { ...size };
    
    if (widgetRef.current) {
      widgetRef.current.style.transition = 'none';
    }
  }, [size, isMobile]);

  const handleResize = useCallback((e: MouseEvent) => {
    if (!isResizing || isMobile) return;

    const deltaX = e.clientX - resizeStartRef.current.x;
    const deltaY = e.clientY - resizeStartRef.current.y;

    const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, initialSizeRef.current.width + deltaX));
    const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, initialSizeRef.current.height + deltaY));

    setSize({ width: newWidth, height: newHeight });

    const maxX = window.innerWidth - newWidth;
    const maxY = window.innerHeight - newHeight;
    const newX = Math.min(positionRef.current.x, maxX);
    const newY = Math.min(positionRef.current.y, maxY);

    updatePosition(newX, newY);
  }, [isResizing, updatePosition, isMobile]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    if (widgetRef.current) {
      widgetRef.current.style.transition = '';
    }
  }, []);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResize);
      window.addEventListener('mouseup', handleResizeEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleResize);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResize, handleResizeEnd]);

  const applyVelocity = useCallback(() => {
    if (!isDraggingRef.current && (Math.abs(velocityRef.current.x) > 0.1 || Math.abs(velocityRef.current.y) > 0.1)) {
      const now = performance.now();
      const deltaTime = (now - lastUpdateTimeRef.current) / 16;
      lastUpdateTimeRef.current = now;

      const friction = 0.95;
      velocityRef.current.x *= friction;
      velocityRef.current.y *= friction;

      const newX = positionRef.current.x + velocityRef.current.x * deltaTime;
      const newY = positionRef.current.y + velocityRef.current.y * deltaTime;
      
      updatePosition(newX, newY);
      animationFrameRef.current = requestAnimationFrame(applyVelocity);
    }
  }, [updatePosition]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isMobile || !headerRef.current?.contains(e.target as Node)) return;

    isDraggingRef.current = true;
    dragStartRef.current = {
      x: e.clientX - positionRef.current.x,
      y: e.clientY - positionRef.current.y
    };
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    lastUpdateTimeRef.current = performance.now();
    velocityRef.current = { x: 0, y: 0 };

    if (headerRef.current) {
      headerRef.current.style.cursor = 'grabbing';
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    e.preventDefault();
  }, [isMobile]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current || isMobile) return;

    const now = performance.now();
    const deltaTime = now - lastUpdateTimeRef.current;
    
    if (deltaTime > 0) {
      velocityRef.current = {
        x: (e.clientX - lastMousePosRef.current.x) / deltaTime * 16,
        y: (e.clientY - lastMousePosRef.current.y) / deltaTime * 16
      };
    }

    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    lastUpdateTimeRef.current = now;

    const newX = e.clientX - dragStartRef.current.x;
    const newY = e.clientY - dragStartRef.current.y;
    
    requestAnimationFrame(() => {
      updatePosition(newX, newY);
    });
  }, [updatePosition, isMobile]);

  const handleMouseUp = useCallback(() => {
    if (!isDraggingRef.current) return;
    
    isDraggingRef.current = false;
    if (headerRef.current) {
      headerRef.current.style.cursor = 'grab';
    }

    animationFrameRef.current = requestAnimationFrame(applyVelocity);
  }, [applyVelocity]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [handleMouseMove, handleMouseUp]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    if (messages.length === 0) {
      const initialMessage = documentContent 
        ? t('chat.greetingWithDoc')
        : t('chat.greeting');

      setMessages([
        {
          role: 'system',
          content: `You are DocHelper AI, a professional document assistant focused on helping users with their documents. ${
            documentContent ? 'You have access to the current document content and can provide specific suggestions.' : ''
          } Be concise, helpful, and professional.`
        },
        {
          role: 'assistant',
          content: initialMessage
        }
      ]);
    }
  }, [messages, documentContent]);

  useEffect(() => {
    if (error) {
      setShowError(true);
      const timer = setTimeout(() => {
        setShowError(false);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    // Check if user can use advanced AI features
    if (canUseAIFeature && !canUseAIFeature('advanced')) {
      setMessages(prev => [...prev, {
        role: 'user',
        content: input.trim()
      }, {
        role: 'assistant',
        content: t('chat.requiresPlan'),
        error: true
      }]);
      setInput('');
      return;
    }

    const userMessage = input.trim();
    setInput('');
    setError(null);
    
    const newMessages = [
      ...messages,
      { role: 'user', content: userMessage }
    ];
    
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const result = await getSuggestion(userMessage, documentContent);
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      if (!result.suggestion) {
        throw new Error(t('chat.noResponse'));
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('chat.suggestionIntro'),
        suggestion: result.suggestion
      }]);
    } catch (error: any) {
      console.error('Error getting AI response:', error);
      const errorMessage = error.message || t('chat.unexpectedError');
      setError(errorMessage);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('chat.errorApology'),
        error: true
      }]);
    } finally {
      setIsLoading(false);
      scrollToBottom();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    
    if (!isMinimized && widgetRef.current) {
      const maxY = window.innerHeight - 60;
      updatePosition(positionRef.current.x, Math.min(positionRef.current.y, maxY));
    }
  };

  return (
    <div 
      ref={widgetRef}
      className={`fixed flex flex-col bg-white dark:bg-gray-800 shadow-2xl z-50 transition-all duration-300 will-change-transform border-2 border-gray-200 dark:border-gray-600 ${
        isMinimized ? 'h-14' : ''
      } ${isMobile ? 'w-full rounded-t-xl' : 'rounded-xl'}`}
      style={{
        transform: `translate3d(${positionRef.current.x}px, ${positionRef.current.y}px, 0)`,
        width: isMinimized ? 300 : (isMobile ? '100%' : size.width),
        height: isMinimized ? 56 : (isMobile ? size.height : size.height),
        ...(isMobile && {
          bottom: 0,
          left: 0,
          right: 0
        }),
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      }}
    >
      <div 
        ref={headerRef}
        className={`p-4 border-b border-gray-200 dark:border-gray-700 ${isMobile ? '' : 'cursor-grab'} select-none bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white rounded-t-xl`}
        onMouseDown={handleMouseDown}
        style={{
          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6, #3b82f6)',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2)'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <MessageSquare className="w-5 h-5" />
            <span className="font-black tracking-wide">{t('chat.title')}</span>
          </div>
          <div className="flex items-center space-x-2">
            {!isMobile && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleMinimize}
                className="hover:bg-white/20 text-white border-white/30"
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="hover:bg-white/20 text-white border-white/30"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {!isMinimized && (
        <>
          {showError && error && (
            <div className="absolute top-16 left-4 right-4 z-50">
              <div className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg text-sm flex items-center shadow-lg animate-fade-in border border-red-300 dark:border-red-700">
                <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                <span className="font-medium">{error}</span>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
            {messages.filter(msg => msg.role !== 'system').map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-xl p-4 shadow-lg ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                      : message.error
                      ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 border border-red-300 dark:border-red-700'
                      : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap font-medium leading-relaxed">{message.content}</p>
                  {message.suggestion && (
                    <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 shadow-inner">
                      <p className="text-sm font-bold mb-2 text-blue-600 dark:text-blue-400">{t('chat.suggestedChange')}</p>
                      <p className="text-sm whitespace-pre-wrap mb-3 text-gray-700 dark:text-gray-300">{message.suggestion}</p>
                      <div className="mt-2">
                        <Button
                          size="sm"
                          onClick={() => onApplySuggestion?.(message.suggestion!)}
                          className="font-bold bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
                        >
                          {t('chat.applySuggestion')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-end space-x-3">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('chat.placeholder')}
                className="flex-1 resize-none rounded-lg border-2 border-gray-300 dark:border-gray-600 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 font-medium transition-all duration-200"
                rows={2}
                disabled={isLoading}
                style={{
                  minHeight: '60px',
                  maxHeight: '120px'
                }}
              />
              <Button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="h-12 w-12 p-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </Button>
            </div>
          </form>

          {!isMobile && (
            <div
              className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize opacity-60 hover:opacity-100 transition-opacity duration-200"
              onMouseDown={handleResizeStart}
              style={{
                background: 'linear-gradient(135deg, transparent 40%, #6b7280 40%, #6b7280 60%, transparent 60%)',
                borderBottomRightRadius: '0.75rem'
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ChatWidget;